/**
 * Mobile GRN — offline-first Goods Receipt (PO and Non-PO).
 * GRN fixes #1 #2 #3 #5 #6 #7 #10 #12 #13 — see memory/GRN_Booking_Fix_Build_Prompt.md.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Camera, CheckCircle2, CloudOff, Wifi, Loader2, AlertTriangle, ListChecks, Pencil, Ban, Eye } from 'lucide-react';
import { apiPost, apiGet, apiPut } from '../../lib/api';
import { cachedGet, cacheReference, getReference, enqueue, remainingPhotoSlots, MAX_OFFLINE_PHOTOS, getRejectedJobs, removeJob, getQueue } from '../../lib/offlineQueue';
import { compressImageFile } from '../stores/stores-constants';
import { POSearchSelect } from '../../components/pickers/POSearchSelect';
import { ProjectSearchSelect } from '../../components/pickers/BOQSearchSelect';
import { VendorNameInput } from '../../components/pickers/VendorNameInput';
import { NonPOItemInput } from '../../components/stores/NonPOItemInput';

const DEFAULT_ITEM = () => ({ description: '', unit: 'NOS', received_qty: '', rate: '', material_id: '', item_mode: 'master', bin_location: '', manufacturing_date: '' });
const EVIDENCE_LABELS = { quantity_check: 'Quantity check', quality_check: 'Quality check', invoice_check: 'Bill / Invoice', mtc_check: 'MTC / Test certificate' };
const LOCS = [['project', 'Project Site'], ['office', 'Office'], ['godown', 'Central Godown']];
const inp = 'w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#3b4872]';
const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function EvidencePhoto({ slotKey, required, photo, onCapture }) {
  const ref = useRef();
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onCapture(slotKey, f); e.target.value = ''; }} />
      <button type="button" onClick={() => ref.current?.click()} data-testid={`grn-photo-${slotKey}`}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed transition ${photo ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 bg-white'}`}>
        {photo
          ? <><CheckCircle2 size={18} className="text-emerald-500 shrink-0" /><span className="text-sm text-emerald-700 font-semibold">{EVIDENCE_LABELS[slotKey]} captured ✓</span></>
          : <><Camera size={18} className="text-slate-400 shrink-0" /><span className="text-sm text-slate-600">Capture {EVIDENCE_LABELS[slotKey]} {required && <span className="text-rose-500">*</span>}</span></>}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>{children}</div>;
}

export default function MobileGRNOffline() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [mode, setMode] = useState('po');
  const [canNonPo, setCanNonPo] = useState(true);
  const [threshold, setThreshold] = useState(5000);
  const [view, setView] = useState('create'); // create | mine

  const [challanNo, setChallanNo] = useState('');
  const [challanDate, setChallanDate] = useState(new Date().toISOString().slice(0, 10));
  const [transporterName, setTransporterName] = useState('');
  const [lrNo, setLrNo] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [images, setImages] = useState({});
  const [checks, setChecks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [photoSlots, setPhotoSlots] = useState(MAX_OFFLINE_PHOTOS);
  const [locType, setLocType] = useState('project');
  const [officeId, setOfficeId] = useState('');
  const [officeName, setOfficeName] = useState('');
  const [offices, setOffices] = useState([]);
  const [reopenJobId, setReopenJobId] = useState(null);
  const [rejectedJobs, setRejectedJobs] = useState([]);

  const [selectedPO, setSelectedPO] = useState(null);
  const [offlinePOs, setOfflinePOs] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [reqIsFallback, setReqIsFallback] = useState(false);
  const [qtyByLine, setQtyByLine] = useState({});
  const [binByLine, setBinByLine] = useState({});
  const [mfgByLine, setMfgByLine] = useState({});

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [vendorPhone, setVendorPhone] = useState('');
  const [vendorGstin, setVendorGstin] = useState('');
  const [items, setItems] = useState([DEFAULT_ITEM()]);

  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const loadRejected = useCallback(() => getRejectedJobs('grn').then(setRejectedJobs).catch(() => {}), []);

  useEffect(() => {
    (async () => {
      // Permission answer is session-only (not the persistent cache) — GRN fix #2.
      try { const d = await apiGet('/stores/grn/non-po-allowed'); setCanNonPo(!!d?.allowed); } catch { /* keep default */ }
      try { const d = await apiGet('/settings/stores-config'); if (d?.non_po_grn_threshold != null) setThreshold(Number(d.non_po_grn_threshold)); } catch { /* default */ }
      try { const { data: pr } = await cachedGet('projects', '/projects'); setProjects(Array.isArray(pr) ? pr : (pr?.projects || [])); } catch { /* no cache */ }
      try { const { data: o } = await cachedGet('office_locations', '/config-masters/office_locations'); setOffices(o?.values || []); } catch { /* no cache */ }
      try {
        if (navigator.onLine) {
          const d = await apiGet('/stores/pos/search', { limit: 50 });
          await cacheReference('grn_po_slim', d.items || []);
          setOfflinePOs(d.items || []);
        } else { setOfflinePOs((await getReference('grn_po_slim')) || []); }
      } catch { setOfflinePOs((await getReference('grn_po_slim').catch(() => null)) || []); }
      setPhotoSlots(await remainingPhotoSlots());
      loadRejected();
    })();
  }, [loadRejected]);

  // GRN fix #13 — reopen a rejected offline job with its saved data + photos.
  useEffect(() => {
    const jid = params.get('job');
    if (!jid) return;
    (async () => {
      const job = (await getQueue()).find(j => j.id === jid);
      if (!job) return;
      const p = job.payload || {};
      setReopenJobId(jid);
      setChallanNo(p.challan_no || ''); setChallanDate(p.challan_date || challanDate);
      setTransporterName(p.transporter_name || ''); setLrNo(p.lr_no || ''); setVehicleNo(p.vehicle_no || '');
      setRemarks(p.remarks || ''); setLocType(p.receiving_location_type || 'project');
      setOfficeId(p.office_location_id || ''); setOfficeName(p.office_location_name || '');
      const imgs = {}; (job.photos || []).forEach(ph => { imgs[ph.slot] = ph.dataUrl; }); setImages(imgs);
      if (p.po_id) {
        setMode('po');
        await selectPO({ id: p.po_id, po_no: job.label?.replace('GRN – PO ', '') || p.po_id, vendor_name: '' });
        const q = {}; (p.items || []).forEach(it => { q[it.boq_id] = String(it.received_qty); }); setQtyByLine(q);
      } else {
        setMode('non_po'); setProjectId(p.project_id || ''); setVendorName(p.vendor_name || ''); setVendorId(p.vendor_id || '');
        setVendorPhone(p.vendor_phone || ''); setVendorGstin(p.vendor_gstin || '');
        setItems((p.items || []).map(it => ({ ...DEFAULT_ITEM(), ...it, received_qty: String(it.received_qty ?? ''), rate: String(it.rate ?? ''), item_mode: it.material_id ? 'master' : 'typed' })));
      }
      toast.info(`Reopened rejected GRN: ${job.lastError || 'fix and resubmit'}`);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const selectPO = async (po) => {
    setSelectedPO(po); setRequirements(null); setReqIsFallback(false); setQtyByLine({}); setImages({}); setChecks({});
    if (!po) return;
    try {
      const { data } = await cachedGet(`grn_req_${po.id}`, `/stores/po/${po.id}/grn-requirements`);
      setRequirements(data);
      const seed = {}; (data.items || []).forEach((ln) => { seed[ln.boq_id] = ln.pending_qty > 0 ? String(ln.pending_qty) : ''; });
      setQtyByLine(seed);
    } catch {
      setReqIsFallback(true);
      setRequirements({ items: [], required_checks: ['quantity_check', 'quality_check', 'invoice_check'], mtc_required: false });
    }
  };

  const capturePhoto = useCallback(async (slot, file) => {
    if (!file) return;
    try {
      const dataUrl = await compressImageFile(file);   // GRN fix #12 — compressed like web
      setImages((prev) => ({ ...prev, [slot]: dataUrl }));
      setChecks((prev) => (slot in prev ? prev : { ...prev, [slot]: true }));
      setPhotoSlots(await remainingPhotoSlots());
    } catch { toast.error('Could not process photo, please retake'); }
  }, []);

  const addItem = () => setItems((is) => [...is, DEFAULT_ITEM()]);
  const removeItem = (i) => setItems((is) => is.filter((_, idx) => idx !== i));
  const updateItem = (i, patch) => setItems((is) => is.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const npTotal = items.reduce((s, it) => s + (parseFloat(it.received_qty) || 0) * (parseFloat(it.rate) || 0), 0);
  const npOver = npTotal > threshold + 0.001;
  const isProjectLoc = locType === 'project';
  const locOk = isProjectLoc ? true : (locType === 'office' ? !!officeId : true);

  const requiredChecksPO = requirements?.required_checks || ['quantity_check', 'quality_check', 'invoice_check'];
  const allChecksPO = requirements?.mtc_required ? [...new Set([...requiredChecksPO, 'mtc_check'])] : requiredChecksPO;
  const missingRequiredPO = requiredChecksPO.filter((c) => !images[c]);
  const poLines = requirements?.items || [];
  const overLines = poLines.filter((ln) => Number(qtyByLine[ln.boq_id] || 0) > Number(ln.pending_qty || 0) + 0.0001);
  const poItemsPayload = poLines
    .map((ln) => ({ boq_id: ln.boq_id, received_qty: Number(qtyByLine[ln.boq_id] || 0), bin_location: (binByLine[ln.boq_id] || '').trim() || null, manufacturing_date: mfgByLine[ln.boq_id] || null }))
    .filter((i) => i.received_qty > 0);
  const logisticsOk = !isProjectLoc || (!!transporterName.trim() && !!lrNo.trim() && !!vehicleNo.trim());
  const canSubmitPO = !!selectedPO && poItemsPayload.length > 0 && missingRequiredPO.length === 0 && overLines.length === 0 && logisticsOk && locOk && !submitting;
  const npValid = items.filter((it) => parseFloat(it.received_qty) > 0 && (it.description.trim() || it.material_id));
  const canSubmitNonPO = (!isProjectLoc || !!projectId) && locOk && !!vendorName.trim() && npValid.length > 0 && !npOver && !!images.invoice_check && !submitting;

  const locPayload = () => ({
    receiving_location_type: locType,
    office_location_id: locType === 'office' ? officeId : undefined,
    office_location_name: locType === 'office' ? officeName : undefined,
  });
  const buildPOPayload = () => ({
    po_id: selectedPO.id, ...locPayload(),
    challan_no: challanNo.trim() || undefined, challan_date: challanDate || undefined,
    transporter_name: transporterName.trim() || undefined, lr_no: lrNo.trim() || undefined, vehicle_no: vehicleNo.trim() || undefined,
    remarks: remarks.trim() || undefined, items: poItemsPayload,
    quantity_check_image: images.quantity_check || undefined, quality_check_image: images.quality_check || undefined,
    invoice_check_image: images.invoice_check || undefined, mtc_check_image: images.mtc_check || undefined,
    quantity_check_checked: !!checks.quantity_check, quality_check_checked: !!checks.quality_check,
    invoice_check_checked: !!checks.invoice_check, mtc_check_checked: !!checks.mtc_check,
  });
  const buildNonPOPayload = () => ({
    project_id: projectId || undefined, ...locPayload(),
    vendor_id: vendorId || undefined, vendor_name: vendorName.trim(),
    vendor_phone: vendorPhone.trim() || undefined, vendor_gstin: vendorGstin.trim() || undefined,
    challan_no: challanNo.trim() || undefined, challan_date: challanDate || undefined,
    transporter_name: transporterName.trim() || undefined, lr_no: lrNo.trim() || undefined, vehicle_no: vehicleNo.trim() || undefined,
    remarks: remarks.trim() || undefined,
    items: npValid.map((it) => ({
      description: it.description.trim() || undefined, unit: it.unit || 'NOS', material_id: it.material_id || undefined,
      received_qty: parseFloat(it.received_qty) || 0, rate: parseFloat(it.rate) || 0,
      bin_location: (it.bin_location || '').trim() || undefined, manufacturing_date: it.manufacturing_date || undefined,
    })),
    invoice_check_image: images.invoice_check || undefined, quantity_check_image: images.quantity_check || undefined,
  });

  const handleSubmit = async () => {
    const isPO = mode === 'po';
    if (isPO ? !canSubmitPO : !canSubmitNonPO) return;
    setSubmitting(true);
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const buildPayload = isPO ? buildPOPayload : buildNonPOPayload;
    const photoKeys = isPO ? allChecksPO : ['invoice_check', 'quantity_check'];
    const label = isPO ? `GRN – PO ${selectedPO?.po_no || ''}` : `GRN – ${vendorName || 'vendor'}`;
    const finish = async () => { if (reopenJobId) await removeJob(reopenJobId).catch(() => {}); navigate('/m'); };

    const queueOffline = async () => {
      const slots = await remainingPhotoSlots();
      const photos = photoKeys.filter((k) => images[k]).map((k) => ({ dataUrl: images[k], name: `${k}.jpg`, slot: k }));
      if (photos.length > slots) { toast.error(`Not enough offline photo slots (${slots} left). Remove a queued entry or wait for sync.`); return false; }
      await enqueue('grn', { label, payload: { ...buildPayload(), client_request_id: genId() }, photos });
      toast.success('Saved offline — will sync when you reconnect');
      await finish();
      return true;
    };

    if (isOffline) { try { await queueOffline(); } catch { toast.error('Could not save offline'); } setSubmitting(false); return; }
    try {
      await apiPost('/stores/grn', { ...buildPayload(), client_request_id: genId() });
      toast.success(isPO ? 'GRN submitted against PO' : 'Non-PO GRN submitted');
      await finish();
    } catch (e) {
      if (e?.response) {
        const d = e.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : (d?.message || 'Could not submit GRN'));
      } else {
        toast.warning('Network error — saving offline');
        try { await queueOffline(); } catch { toast.error('Could not save offline either — check storage space'); }
      }
    }
    setSubmitting(false);
  };

  const LocationPicker = (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="grn-receiving-location">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Receiving location</div>
      <div className="flex gap-2">
        {LOCS.map(([v, l]) => (
          <button key={v} type="button" data-testid={`grn-loc-${v}`} onClick={() => { setLocType(v); setOfficeId(''); setOfficeName(''); }}
            className={`flex-1 px-2 py-2 text-[11px] font-bold rounded-lg border ${locType === v ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300'}`}>{l}</button>
        ))}
      </div>
      {locType === 'office' && (
        <select value={officeId} data-testid="grn-office-location-select" className={`${inp} mt-2`}
          onChange={(e) => { const o = offices.find(x => x.id === e.target.value); setOfficeId(e.target.value); setOfficeName(o?.name || ''); }}>
          <option value="">Select office location *</option>
          {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
    </div>
  );
  const Logistics = ({ req, prefix }) => (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Field label={`Transporter Name${req ? ' *' : ''}`}><input value={transporterName} onChange={(e) => setTransporterName(e.target.value)} placeholder={req ? 'e.g. Shree Cargo Movers' : 'Optional'} className={inp} data-testid={`${prefix}transporter-name`} /></Field></div>
      <Field label={`LR No.${req ? ' *' : ''}`}><input value={lrNo} onChange={(e) => setLrNo(e.target.value)} placeholder={req ? 'LR-4521' : 'Optional'} className={inp} data-testid={`${prefix}lr-no`} /></Field>
      <Field label={`Vehicle No.${req ? ' *' : ''}`}><input value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder={req ? 'MH-12-AB-1234' : 'Optional'} className={inp} data-testid={`${prefix}vehicle-no`} /></Field>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="mobile-grn-offline">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/m')} className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-slate-500" data-testid="m-grn-back"><ArrowLeft size={14} /> Back</button>
        <h1 className="font-display font-bold text-sm uppercase tracking-wide">Goods Receipt</h1>
        <div className={`flex items-center gap-1 text-[10px] font-bold ${online ? 'text-emerald-600' : 'text-amber-600'}`}>{online ? <Wifi size={12} /> : <CloudOff size={12} />}{online ? 'Online' : 'Offline'}</div>
      </header>

      <div className="px-4 pb-56 pt-4 space-y-4 max-w-lg mx-auto">
        <div className="flex gap-2">
          <button type="button" onClick={() => setView('create')} data-testid="grn-view-create" className={`flex-1 py-2 text-xs font-bold rounded-lg border ${view === 'create' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}>New GRN</button>
          <button type="button" onClick={() => setView('mine')} data-testid="grn-view-mine" className={`flex-1 py-2 text-xs font-bold rounded-lg border flex items-center justify-center gap-1 ${view === 'mine' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}><ListChecks size={13} /> My recent GRNs</button>
        </div>

        {rejectedJobs.length > 0 && view === 'create' && !reopenJobId && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-2" data-testid="grn-rejected-jobs">
            <div className="flex items-center gap-1.5 text-xs font-bold text-rose-800"><AlertTriangle size={13} /> {rejectedJobs.length} GRN{rejectedJobs.length > 1 ? 's' : ''} need{rejectedJobs.length > 1 ? '' : 's'} attention</div>
            {rejectedJobs.map(j => (
              <div key={j.id} className="bg-white rounded-md p-2 text-[11px]" data-testid={`grn-rejected-job-${j.id}`}>
                <div className="font-semibold">{j.label}</div>
                <div className="text-rose-700">{j.lastError}</div>
                <div className="flex gap-3 mt-1">
                  <button type="button" onClick={() => navigate(`/m/grn?job=${j.id}`)} className="font-bold text-[#3b4872] underline" data-testid={`grn-rejected-open-${j.id}`}>Open</button>
                  <button type="button" onClick={async () => { await removeJob(j.id); loadRejected(); toast.success('Discarded'); }} className="font-bold text-slate-500 underline" data-testid={`grn-rejected-discard-${j.id}`}>Discard</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {view === 'mine' ? <MyRecentGRNs /> : (<>
          {!online && <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800" data-testid="grn-offline-notice"><CloudOff size={14} className="shrink-0 mt-0.5" /><span>You&apos;re offline. This GRN will be saved on your device and synced when you reconnect.</span></div>}
          {reopenJobId && <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 text-[11px] text-sky-800" data-testid="grn-reopen-banner">Editing a rejected offline GRN — fix the issue and submit again; the old entry is removed on success.</div>}

          <div className="flex gap-2" data-testid="grn-mode-toggle">
            <button type="button" onClick={() => setMode('po')} data-testid="grn-mode-po" className={`flex-1 px-3 py-2 text-xs font-display font-bold uppercase tracking-wide rounded-lg border ${mode === 'po' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300'}`}>Receive against PO</button>
            <button type="button" onClick={() => canNonPo && setMode('non_po')} disabled={!canNonPo} data-testid="grn-mode-nonpo"
              title={canNonPo ? '' : 'Ask your admin for GRN write access'}
              className={`flex-1 px-3 py-2 text-xs font-display font-bold uppercase tracking-wide rounded-lg border disabled:opacity-50 ${mode === 'non_po' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-700 border-amber-300'}`}>
              Non-PO (petty, up to ₹{threshold.toLocaleString('en-IN')})
            </button>
          </div>
          {!canNonPo && <p className="text-[11px] text-slate-500" data-testid="grn-nonpo-denied">Non-PO needs GRN write access — ask your admin.</p>}
          {mode === 'non_po' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[11px] text-amber-800" data-testid="grn-np-cap-note">
              For small purchases without a PO (nails, consumables…). Capped at <b>₹{threshold.toLocaleString('en-IN')}</b> per GRN — anything higher must go through a Purchase Order.
            </div>
          )}

          {LocationPicker}

          {mode === 'po' ? (<>
            <Field label="Purchase Order *">
              <POSearchSelect value={selectedPO} onSelect={selectPO} offline={!online} offlineRows={offlinePOs} testid="grn-po-select" />
            </Field>
            {selectedPO && (<>
              {reqIsFallback && <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[11px] text-amber-700">Offline and this PO was never opened online — its lines are not cached. Reconnect to load them.</div>}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Challan / DC No."><input value={challanNo} onChange={(e) => setChallanNo(e.target.value)} placeholder="DC-12345" className={inp} data-testid="grn-challan-no" /></Field>
                <Field label="Challan Date"><input type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)} className={inp} data-testid="grn-challan-date" /></Field>
              </div>
              <Logistics req={isProjectLoc} prefix="grn-" />
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-2">Items — quantity defaults to pending</label>
                <div className="space-y-2">
                  {poLines.map((ln) => {
                    const qty = qtyByLine[ln.boq_id] ?? '';
                    const pending = Number(ln.pending_qty || 0);
                    const over = Number(qty || 0) > pending + 0.0001;
                    return (
                      <div key={ln.boq_id} className={`bg-white border rounded-lg p-3 ${over ? 'border-rose-300' : 'border-slate-200'}`} data-testid={`grn-po-line-${ln.boq_id}`}>
                        <div className="text-sm font-medium text-slate-800">{ln.item_code && <span className="font-mono text-[11px] text-slate-500 mr-2">{ln.item_code}</span>}{ln.description}</div>
                        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                          <span>Ordered {ln.quantity}</span><span>Received {ln.received_qty}</span>
                          <span className="text-emerald-700 font-semibold">Pending {pending}</span>
                          {Number(ln.rejected_qty || 0) > 0 && <span className="text-rose-600" data-testid={`grn-rejected-${ln.boq_id}`}>{ln.rejected_qty} rejected, replacement due</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <input type="number" min="0" step="any" value={qty} onChange={(e) => setQtyByLine((prev) => ({ ...prev, [ln.boq_id]: e.target.value }))}
                            placeholder="Qty received" data-testid={`grn-po-line-qty-${ln.boq_id}`} className={`flex-1 border rounded-md px-2.5 py-2 text-sm focus:outline-none ${over ? 'border-rose-400 bg-rose-50' : 'border-slate-300'}`} />
                          <span className="text-xs text-slate-400">{ln.unit}</span>
                        </div>
                        {over && <p className="text-[11px] text-rose-600 mt-1 font-semibold" data-testid={`grn-po-line-over-${ln.boq_id}`}>Max {pending} pending</p>}
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <input value={binByLine[ln.boq_id] || ''} onChange={(e) => setBinByLine(p => ({ ...p, [ln.boq_id]: e.target.value }))} placeholder="Bin / Location" className="border border-slate-300 rounded-md px-2.5 py-1.5 text-xs" data-testid={`grn-po-line-bin-${ln.boq_id}`} />
                          <input type="date" value={mfgByLine[ln.boq_id] || ''} onChange={(e) => setMfgByLine(p => ({ ...p, [ln.boq_id]: e.target.value }))} className="border border-slate-300 rounded-md px-2.5 py-1.5 text-xs" data-testid={`grn-po-line-mfg-${ln.boq_id}`} title="Mfg. date" />
                        </div>
                      </div>
                    );
                  })}
                  {poLines.length === 0 && !reqIsFallback && <p className="text-xs text-slate-400">Loading PO lines…</p>}
                </div>
              </div>
              <div className="space-y-3">
                <div className="text-xs font-semibold text-slate-600">Photos {!online && <span className="ml-2 text-amber-600 font-normal">({photoSlots} offline slot{photoSlots !== 1 ? 's' : ''} available)</span>}</div>
                {allChecksPO.map((c) => <EvidencePhoto key={c} slotKey={c} required={requiredChecksPO.includes(c)} photo={images[c]} onCapture={capturePhoto} />)}
              </div>
              <Field label="Remarks"><textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Condition of goods, partial delivery, etc." className={`${inp} resize-none`} data-testid="grn-remarks" /></Field>
            </>)}
          </>) : (<>
            {isProjectLoc && <Field label="Project *"><ProjectSearchSelect projects={projects} value={projectId} onSelect={setProjectId} testid="grn-project-select" /></Field>}
            <Field label="Shop / vendor name *">
              <VendorNameInput name={vendorName} vendorId={vendorId} onChange={({ name, vendorId: vid, vendor }) => { setVendorName(name); setVendorId(vid || ''); if (vendor?.gstin) setVendorGstin(vendor.gstin); }} testid="grn-vendor-input" compact />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Shop phone"><input value={vendorPhone} onChange={(e) => setVendorPhone(e.target.value)} placeholder="Optional" className={inp} data-testid="grn-vendor-phone" /></Field>
              <Field label="GSTIN (if on bill)"><input value={vendorGstin} onChange={(e) => setVendorGstin(e.target.value.toUpperCase())} placeholder="Optional" className={inp} data-testid="grn-vendor-gstin" /></Field>
              <Field label="Bill No."><input value={challanNo} onChange={(e) => setChallanNo(e.target.value)} placeholder="Cash bill #" className={inp} data-testid="grn-challan-no" /></Field>
              <Field label="Bill Date"><input type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)} className={inp} data-testid="grn-challan-date" /></Field>
            </div>
            <Logistics req={false} prefix="grn-np-" />
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-600">Items Received *</label>
                <button onClick={addItem} className="flex items-center gap-1 text-xs text-[#3b4872] font-bold" data-testid="grn-add-item"><Plus size={13} /> Add item</button>
              </div>
              <div className="space-y-3">
                {items.map((it, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`grn-item-${i}`}>
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 min-w-0"><NonPOItemInput line={it} onChange={(patch) => updateItem(i, patch)} testid={`grn-item-${i}`} compact={false} /></div>
                      {items.length > 1 && <button onClick={() => removeItem(i)} className="text-rose-400 p-1.5" data-testid={`grn-item-remove-${i}`}><Trash2 size={14} /></button>}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <input value={it.received_qty} onChange={(e) => updateItem(i, { received_qty: e.target.value })} type="number" min="0" step="any" placeholder="Qty *" className="border border-slate-300 rounded-md px-2.5 py-2 text-sm" data-testid={`grn-item-qty-${i}`} />
                      <input value={it.unit} onChange={(e) => updateItem(i, { unit: e.target.value })} placeholder="Unit" className="border border-slate-300 rounded-md px-2.5 py-2 text-sm" data-testid={`grn-item-unit-${i}`} />
                      <input value={it.rate} onChange={(e) => updateItem(i, { rate: e.target.value })} type="number" min="0" step="any" placeholder="Rate ₹" className="border border-slate-300 rounded-md px-2.5 py-2 text-sm" data-testid={`grn-item-rate-${i}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={it.bin_location} onChange={(e) => updateItem(i, { bin_location: e.target.value })} placeholder="Bin / Location" className="border border-slate-300 rounded-md px-2.5 py-1.5 text-xs" data-testid={`grn-item-bin-${i}`} />
                      <input type="date" value={it.manufacturing_date} onChange={(e) => updateItem(i, { manufacturing_date: e.target.value })} className="border border-slate-300 rounded-md px-2.5 py-1.5 text-xs" data-testid={`grn-item-mfg-${i}`} title="Mfg. date" />
                    </div>
                  </div>
                ))}
              </div>
              <div className={`flex justify-between text-xs mt-1.5 font-semibold ${npOver ? 'text-rose-600' : 'text-slate-600'}`} data-testid="grn-np-total">
                <span>Total ₹{npTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span><span className="font-normal text-slate-400">Limit ₹{threshold.toLocaleString('en-IN')}</span>
              </div>
              {npOver && <p className="text-[11px] text-rose-600 mt-1" data-testid="grn-np-over-limit">Non-PO GRN total ₹{Math.round(npTotal).toLocaleString('en-IN')} exceeds the ₹{threshold.toLocaleString('en-IN')} limit — please raise a Purchase Order instead.</p>}
            </div>
            <div className="space-y-3">
              <div className="text-xs font-semibold text-slate-600">Photos {!online && <span className="ml-2 text-amber-600 font-normal">({photoSlots} offline slot{photoSlots !== 1 ? 's' : ''} available)</span>}</div>
              <EvidencePhoto slotKey="invoice_check" required photo={images.invoice_check} onCapture={capturePhoto} />
              <EvidencePhoto slotKey="quantity_check" required={false} photo={images.quantity_check} onCapture={capturePhoto} />
            </div>
            <Field label="Remarks"><textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Condition of goods, etc." className={`${inp} resize-none`} data-testid="grn-remarks" /></Field>
          </>)}
        </>)}
      </div>

      {view === 'create' && (
        <div className="fixed left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 z-50" style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))', paddingBottom: '4px' }}>
          <button onClick={handleSubmit} disabled={mode === 'po' ? !canSubmitPO : !canSubmitNonPO} data-testid="grn-submit-btn"
            className={`w-full font-display font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-40 transition-colors text-white ${mode === 'po' ? 'bg-[#3b4872] hover:bg-[#2d3856]' : 'bg-amber-600 hover:bg-amber-700'}`}>
            {submitting ? <Loader2 size={16} className="animate-spin" /> : online ? <Wifi size={16} /> : <CloudOff size={16} />}
            {submitting ? 'Submitting…' : online ? 'Submit GRN' : 'Save Offline'}
          </button>
          {mode === 'po' && selectedPO && missingRequiredPO.length > 0 && <p className="text-center text-xs text-slate-400 mt-1.5">{missingRequiredPO.map((c) => EVIDENCE_LABELS[c]).join(', ')} required before submitting</p>}
          {mode === 'po' && overLines.length > 0 && <p className="text-center text-xs text-rose-600 mt-1.5">One or more lines exceed the pending quantity</p>}
          {mode === 'non_po' && !images.invoice_check && <p className="text-center text-xs text-slate-400 mt-1.5">Bill / invoice photo required before submitting</p>}
        </div>
      )}
    </div>
  );
}

/** GRN fix #10 (mobile) — last 20 GRNs created by me, with view / edit / cancel while QC-pending. */
function MyRecentGRNs() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null); // {grn, mode: 'view'|'edit'|'cancel'}
  const [reason, setReason] = useState('');
  const [editQty, setEditQty] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => apiGet('/stores/grn', { mine: true, limit: 20 }).then(d => setRows(d.items || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const editable = (g) => g.status !== 'cancelled' && ['pending', 'awaiting'].includes(g.inspection_status || 'awaiting');
  const openEdit = (g) => { const q = {}; (g.items || []).forEach((it, i) => { q[i] = String(it.received_qty); }); setEditQty(q); setActive({ grn: g, mode: 'edit' }); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await apiPut(`/stores/grn/${active.grn.id}`, { items: (active.grn.items || []).map((it, i) => ({ boq_id: it.boq_id || null, received_qty: Number(editQty[i] || 0) })) });
      toast.success('GRN updated'); setActive(null); load();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Could not save'); } finally { setBusy(false); }
  };
  const doCancel = async () => {
    if (reason.trim().length < 3) { toast.error('Enter a reason'); return; }
    setBusy(true);
    try { await apiPost(`/stores/grn/${active.grn.id}/cancel`, { reason: reason.trim() }); toast.success('GRN cancelled'); setActive(null); setReason(''); load(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Could not cancel'); } finally { setBusy(false); }
  };
  if (rows === null) return <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading…</p>;
  if (rows.length === 0) return <p className="text-xs text-slate-400" data-testid="grn-mine-empty">You have not created any GRNs yet.</p>;
  return (
    <div className="space-y-2" data-testid="grn-mine-list">
      {rows.map(g => (
        <div key={g.id} className={`bg-white border border-slate-200 rounded-lg p-3 ${g.status === 'cancelled' ? 'opacity-60' : ''}`} data-testid={`grn-mine-${g.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-mono text-sm font-bold flex items-center gap-1.5">{g.grn_no}{g.is_non_po && <span className="px-1 rounded text-[9px] bg-violet-100 text-violet-800">NON-PO</span>}{g.status === 'cancelled' && <span className="px-1 rounded text-[9px] bg-slate-200 text-slate-700">CANCELLED</span>}</div>
              <div className="text-[11px] text-slate-500 truncate">{g.vendor_name} · ₹{Number(g.total_value || 0).toLocaleString('en-IN')} · {g.status === 'cancelled' ? 'cancelled' : (g.inspection_status === 'inspected' ? `QC ${g.inspection_decision?.replace('_', ' ')}` : 'QC pending')}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button type="button" onClick={() => setActive({ grn: g, mode: 'view' })} className="p-1.5 text-slate-500" data-testid={`grn-mine-view-${g.id}`}><Eye size={15} /></button>
              {editable(g) && <button type="button" onClick={() => openEdit(g)} className="p-1.5 text-[#3b4872]" data-testid={`grn-mine-edit-${g.id}`}><Pencil size={15} /></button>}
              {editable(g) && <button type="button" onClick={() => setActive({ grn: g, mode: 'cancel' })} className="p-1.5 text-rose-600" data-testid={`grn-mine-cancel-${g.id}`}><Ban size={15} /></button>}
            </div>
          </div>
          {active?.grn.id === g.id && (
            <div className="mt-2 border-t pt-2 text-xs space-y-2" data-testid={`grn-mine-panel-${g.id}`}>
              {(g.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <span className="truncate">{it.description}</span>
                  {active.mode === 'edit'
                    ? <input type="number" min="0" value={editQty[i] ?? ''} onChange={e => setEditQty(p => ({ ...p, [i]: e.target.value }))} className="w-20 border rounded px-1.5 py-1 text-right" data-testid={`grn-mine-edit-qty-${i}`} />
                    : <span className="tabular-nums">{it.received_qty} {it.unit}</span>}
                </div>
              ))}
              {active.mode === 'cancel' && <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Reason for cancellation *" className="w-full border border-rose-200 rounded px-2 py-1.5" data-testid="grn-mine-cancel-reason" />}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setActive(null)} className="px-3 py-1.5 rounded border text-slate-600">Close</button>
                {active.mode === 'edit' && <button type="button" disabled={busy} onClick={saveEdit} className="px-3 py-1.5 rounded bg-[#3b4872] text-white font-bold" data-testid="grn-mine-save">Save</button>}
                {active.mode === 'cancel' && <button type="button" disabled={busy} onClick={doCancel} className="px-3 py-1.5 rounded bg-rose-600 text-white font-bold" data-testid="grn-mine-cancel-confirm">Confirm cancel</button>}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
